import * as StateMachine from 'javascript-state-machine';

import type {HeartbeatService} from '../core/HeartbeatService';
import {Event, JSM_INITIAL_EVENT} from '../enums/Event';
import {createVideoStartFailedReason} from '../enums/VideoStartFailedReason';
import type {StateMachineCallbacks} from '../types/StateMachineCallbacks';
import {logger} from '../utils/Logger';
import {isFunction} from '../utils/Utils';

import {AnalyticsStateMachine} from './AnalyticsStateMachine';
import {
  customStateMachineErrorCallback,
  logMissingCallbackWarning,
  logStateMachineTransition,
  on,
  whileIn,
} from './stateMachineUtils';

export enum State {
  AD = 'AD',
  AUDIOTRACK_CHANGED_PLAYING = 'AUDIOTRACK_CHANGED_PLAYING',
  AUDIOTRACK_CHANGING = 'AUDIOTRACK_CHANGING',
  BUFFERING = 'BUFFERING',
  CASTING = 'CASTING',
  /**
   * This state was created only to be able to "cut" the {@link State.PLAYING} and {@link State.PAUSE}
   * and send playing/pause sample with current custom data, while calling {@link AdapterAPI.setCustomData} function
   * and setting the new custom data values.
   *
   * @remarks
   *
   * Our implementation relays on a specific state name "CUSTOMDATACHANGE" because it should call the
   * default "onleavestate" state machine callback {@link stateMachineCallbacks.customdatachange}.
   */
  CUSTOMDATACHANGE = 'CUSTOMDATACHANGE',
  END = 'END',
  END_PLAY_SEEKING = 'END_PLAY_SEEKING',
  ERROR = 'ERROR',
  MUTING_PAUSE = 'MUTING_PAUSE',
  MUTING_PLAY = 'MUTING_PLAY',
  MUTING_READY = 'MUTING_READY',
  PAUSE = 'PAUSE',
  PAUSED_SEEKING = 'PAUSED_SEEKING',
  PLAY_SEEKING = 'PLAY_SEEKING',
  PLAYING = 'PLAYING',
  QUALITY_CHANGE = 'QUALITY_CHANGE',
  READY = 'READY',
  SETUP = 'SETUP',
  SOURCE_CHANGING = 'SOURCE_CHANGING',
  STARTUP = 'STARTUP',
  SUBTITLE_CHANGED_PLAYING = 'SUBTITLE_CHANGED_PLAYING',
  SUBTITLE_CHANGING = 'SUBTITLE_CHANGING',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

export class VideojsAnalyticsStateMachine extends AnalyticsStateMachine {
  private seekTimestamp: number;
  private seekedTimestamp: number;

  constructor(stateMachineCallbacks: StateMachineCallbacks, playingHeartbeatService: HeartbeatService) {
    super(stateMachineCallbacks, playingHeartbeatService);

    this.seekTimestamp = 0;
    this.seekedTimestamp = 0;
  }

  getAllStates() {
    return [...Object.keys(State).map((key) => State[key]), 'FINISH_PLAY_SEEKING', 'PLAY_SEEK'];
  }

  getAllStatesBut(state: string) {
    return this.getAllStates().filter((i) => i !== state);
  }

  override createStateMachine() {
    return StateMachine.create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        {name: Event.SOURCE_LOADED, from: [State.SETUP, State.ERROR, State.SOURCE_CHANGING], to: State.READY},
        on(Event.TIMECHANGED).stayIn(State.SETUP),
        on(Event.SOURCE_LOADED).stayIn(State.READY),
        {name: Event.READY, from: [State.SETUP, State.ERROR], to: State.READY},
        {name: Event.PLAY, from: State.READY, to: State.STARTUP},

        {name: Event.ERROR, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.UNLOAD, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.VIDEOSTART_TIMEOUT, from: State.STARTUP, to: State.VIDEOSTART_FAILED},

        on(Event.START_BUFFERING).stayIn(State.STARTUP),
        on(Event.END_BUFFERING).stayIn(State.STARTUP),
        {name: Event.TIMECHANGED, from: State.STARTUP, to: State.PLAYING},

        on(Event.TIMECHANGED).stayIn(State.PLAYING),
        on(Event.END_BUFFERING).stayIn(State.PLAYING),
        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.BUFFERING},
        on(Event.START_BUFFERING).stayIn(State.BUFFERING),
        {name: Event.TIMECHANGED, from: State.BUFFERING, to: State.PLAYING},

        // Ignoring since it's pushed in a live stream
        on(Event.SEEK).stayIn(State.STARTUP),
        {name: Event.SEEK, from: State.PLAYING, to: State.PLAY_SEEKING},
        on(Event.TIMECHANGED).stayIn(State.PLAY_SEEKING),
        on(Event.TIMECHANGED).stayIn(State.PAUSED_SEEKING),

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PAUSE, from: State.BUFFERING, to: State.PAUSE},
        on(Event.TIMECHANGED).stayIn(State.PAUSE),
        {name: Event.PLAY, from: State.PAUSE, to: State.PLAYING},

        {name: Event.VIDEO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.AUDIO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.FINISH_QUALITY_CHANGE, from: State.QUALITY_CHANGE, to: State.PLAYING},

        {name: Event.SEEK, from: State.PAUSE, to: State.PAUSED_SEEKING},
        on(Event.SEEK).stayIn(State.PAUSED_SEEKING),
        on(Event.START_BUFFERING).stayIn(State.PAUSED_SEEKING),
        on(Event.END_BUFFERING).stayIn(State.PAUSED_SEEKING),
        {name: Event.SEEKED, from: State.PAUSED_SEEKING, to: State.PAUSE},
        {name: Event.PLAY, from: State.PAUSED_SEEKING, to: State.PLAYING},
        {name: Event.PAUSE, from: State.PAUSED_SEEKING, to: State.PAUSE},

        {name: 'PLAY_SEEK', from: State.PAUSE, to: State.PLAY_SEEKING},
        {name: 'PLAY_SEEK', from: State.PAUSED_SEEKING, to: State.PLAY_SEEKING},
        on('PLAY_SEEK').stayIn(State.PLAY_SEEKING),
        on(Event.SEEK).stayIn(State.PLAY_SEEKING),
        on(Event.START_BUFFERING).stayIn(State.PLAY_SEEKING),
        on(Event.END_BUFFERING).stayIn(State.PLAY_SEEKING),
        on(Event.SEEKED).stayIn(State.PLAY_SEEKING),

        // We are ending the seek
        {name: Event.SEEKED, from: State.PLAY_SEEKING, to: State.END_PLAY_SEEKING},

        on(Event.START_BUFFERING).stayIn(State.END_PLAY_SEEKING),
        on(Event.END_BUFFERING).stayIn(State.END_PLAY_SEEKING),
        on(Event.SEEKED).stayIn(State.END_PLAY_SEEKING),
        {name: Event.TIMECHANGED, from: State.END_PLAY_SEEKING, to: State.PLAYING},

        {name: Event.END, from: State.PLAY_SEEKING, to: State.END},
        {name: Event.END, from: State.PAUSED_SEEKING, to: State.END},
        {name: Event.END, from: State.PLAYING, to: State.END},
        {name: Event.END, from: State.PAUSE, to: State.END},
        on(Event.SEEK).stayIn(State.END),
        on(Event.SEEKED).stayIn(State.END),
        on(Event.TIMECHANGED).stayIn(State.END),
        on(Event.END_BUFFERING).stayIn(State.END),
        on(Event.START_BUFFERING).stayIn(State.END),
        on(Event.END).stayIn(State.END),

        // Ignored - Livestreams do a Seek during startup and SEEKED once playback started
        on(Event.SEEKED).stayIn(State.PLAYING),

        {name: Event.PLAY, from: State.END, to: State.PLAYING},

        {name: Event.ERROR, from: this.getAllStatesBut(State.STARTUP), to: State.ERROR},
        {name: Event.PAUSE, from: State.ERROR, to: State.ERROR},

        {name: Event.SEEK, from: State.END_PLAY_SEEKING, to: State.PLAY_SEEKING},
        {name: 'FINISH_PLAY_SEEKING', from: State.END_PLAY_SEEKING, to: State.PLAYING},

        {name: Event.UNLOAD, from: this.getAllStatesBut(State.STARTUP), to: State.END},

        {name: Event.START_AD, from: State.PLAYING, to: State.AD},
        {name: Event.END_AD, from: State.AD, to: State.PLAYING},

        // in scenario, where VideoJS player is initialized with `muted: true` option, VideoJS player
        // triggers PlayerEvent `volumechange` before PlayerEvent `ready`
        on(Event.MUTE).stayIn(State.SETUP),

        {name: Event.MUTE, from: State.READY, to: State.MUTING_READY},
        {name: Event.UN_MUTE, from: State.READY, to: State.MUTING_READY},
        {name: 'FINISH_MUTING', from: State.MUTING_READY, to: State.READY},

        {name: Event.MUTE, from: State.PLAYING, to: State.MUTING_PLAY},
        {name: Event.UN_MUTE, from: State.PLAYING, to: State.MUTING_PLAY},
        {name: 'FINISH_MUTING', from: State.MUTING_PLAY, to: State.PLAYING},

        {name: Event.MUTE, from: State.PAUSE, to: State.MUTING_PAUSE},
        {name: Event.UN_MUTE, from: State.PAUSE, to: State.MUTING_PAUSE},
        {name: 'FINISH_MUTING', from: State.MUTING_PAUSE, to: State.PAUSE},

        {name: Event.START_CAST, from: [State.READY, State.PAUSE], to: State.CASTING},
        on(Event.PAUSE).stayIn(State.CASTING),
        on(Event.PLAY).stayIn(State.CASTING),
        on(Event.TIMECHANGED).stayIn(State.CASTING),
        on(Event.MUTE).stayIn(State.CASTING),
        on(Event.SEEK).stayIn(State.CASTING),
        on(Event.SEEKED).stayIn(State.CASTING),
        {name: Event.END_CAST, from: State.CASTING, to: State.READY},

        on(Event.SEEK).stayIn(State.READY),
        on(Event.SEEKED).stayIn(State.READY),
        {name: Event.SEEKED, from: State.STARTUP, to: State.STARTUP},

        {name: Event.AUDIOTRACK_CHANGED, from: State.PLAYING, to: State.AUDIOTRACK_CHANGED_PLAYING},
        {name: 'FINISH_AUDIOTRACK_CHANGING', from: State.AUDIOTRACK_CHANGED_PLAYING, to: State.PLAYING},
        // States which should ignore audioTrack changes
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.PAUSE),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.READY),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.BUFFERING),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.END_PLAY_SEEKING),
        {name: Event.AUDIOTRACK_CHANGED, from: State.AUDIOTRACK_CHANGING, to: State.AUDIOTRACK_CHANGING},

        {name: Event.SUBTITLE_CHANGE, from: State.PLAYING, to: State.SUBTITLE_CHANGED_PLAYING},
        {name: 'FINISH_SUBTITLE_CHANGING', from: State.SUBTITLE_CHANGED_PLAYING, to: State.PLAYING},
        // States which should ignore subtitle changes
        on(Event.SUBTITLE_CHANGE).stayIn(State.PAUSE),
        on(Event.SUBTITLE_CHANGE).stayIn(State.READY),
        on(Event.SUBTITLE_CHANGE).stayIn(State.BUFFERING),
        on(Event.SUBTITLE_CHANGE).stayIn(State.END_PLAY_SEEKING),
        on(Event.SUBTITLE_CHANGE).stayIn(State.SOURCE_CHANGING),
        on(Event.SUBTITLE_CHANGE).stayIn(State.SUBTITLE_CHANGING),

        {name: Event.MANUAL_SOURCE_CHANGE, from: this.getAllStatesBut(State.READY), to: State.SOURCE_CHANGING},
        // Case: ADAPTER API SOURCE CHANGE VS. PLAYER SOURCE CHANGE
        // Problem:
        //    The Event.MANUAL_SOURCE_CHANGE is fired in two cases:
        //    1) when the player source change is detected by InternalAdapter
        //    2) when the AdapterAPI.changeSource() is called by the customer
        //    This means that we can have two consecutive Event.MANUAL_SOURCE_CHANGE events in possible two orders.
        //
        //    AdapterAPI.changeSource() call is optional and should be called before the attempt for player source change!!
        //    It supposes to let us know that the player source change will be triggered, in case when we can't automatically
        //    detect it from the player events (e.g., HTML native adapter) and mostly to be able to pass the new
        //    AnalyticsConfig for the new source.
        //
        //    But because its call is implemented by the customer, we can't be sure about the order of these two events.
        // Solution:
        //    1) We want to mostly support the order `(optional) AdapterAPI.changeSource() -> player source change`.
        //    This should be communicated to the customer as the recommended way because it is more intuitive to call
        //    AdapterAPI.changeSource() before doing the player source change.
        //
        //    2) We also want to support "partially" the opposite order `player source change -> AdapterAPI.changeSource()`,
        //    BUT only in the case when the AdapterAPI.changeSource() is called while we are already in State.SOURCE_CHANGING,
        //    which means that the player source change has already started, and we are waiting for the source to be loaded.
        //    Why? Because in some customer integrations, the customer can't call AdapterAPI.changeSource() "proactively"
        //    (e.g., platforms like React Native, Chromecast) but rather "reactively" in response to the player event that
        //    we also relay on to detect the player source change. That means that the customer player event handler
        //    is called always after ours.
        ...whileIn(State.SOURCE_CHANGING).passOver(Event.MANUAL_SOURCE_CHANGE),

        on(Event.MANUAL_SOURCE_CHANGE).stayIn(State.READY),
        on(Event.MANUAL_SOURCE_CHANGE).stayIn(State.SETUP),
        on(Event.TIMECHANGED).stayIn(State.SOURCE_CHANGING),

        // Context: videojs `play()` can be called before source is loaded
        //
        //    This means that we should transition to STARTUP state already, even while source is still loading,
        //    to be able to measure the startup time correctly. Because once video is loaded, it will start playing
        //    and player event 'timeupdate' will be emitted.
        //
        //    If we would not do this state transition, player event 'loadedmetadata' (SOURCE_LOADED) would move us
        //    to READY state, which would be incorrect, because:
        //     - the most important, we would not be able to measure the time from play() to first frame as startup time
        //     - we would be stuck in READY state, and not able to move to state PLAYING, on emitted player event
        //       'timeupdate', because such transition doesn't exist in the state machine (STARTUP state is required)
        {name: Event.PLAY, from: State.SOURCE_CHANGING, to: State.STARTUP},
        // Context: videojs `play()` can be called before source is loaded
        //
        //    This means that when source is loaded, and `play()` was already called, so we are in STARTUP state and
        //    measuring startup time, we should stay in STARTUP state, because player event 'timeupdate' will be emitted,
        //    and it will move us to PLAYING state later.
        on(Event.SOURCE_LOADED).stayIn(State.STARTUP),

        // Because of this state transition, we are able to male "a cut" in the current PLAYING / PAUSE state
        // and send "playing" / "pause" sample with the current custom data values. The new changed custom data values
        // will be set on upcoming samples after we transition back from the CUSTOMDATACHANGE state.
        // For any other states, we don't care, because InternalAdapter is not emitting the event.
        {name: Event.CUSTOM_DATA_CHANGE, from: [State.PLAYING, State.PAUSE], to: State.CUSTOMDATACHANGE},
        {name: Event.PLAYING, from: State.CUSTOMDATACHANGE, to: State.PLAYING},
        {name: Event.PAUSE, from: State.CUSTOMDATACHANGE, to: State.PAUSE},
      ],

      // Keep callbacks sorted by order in which they are called by JSM library (and after order, sorted by name)
      //
      // Do not use convenience, shortened callbacks
      //  - instead of `onEVENT` use `onafterEVENT`
      //  - instead of `onSTATE` use `onenterSTATE`
      //
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#callbacks
      callbacks: {
        [`onleave${State.PLAYING}`]: () => {
          this.playingHeartbeatService.stopHeartbeat();
        },

        [`onleave${State.STARTUP}`]: (_event, _from, to, _eventTimestamp: number, _eventData) => {
          if (to === State.PLAYING) {
            this.startupHasSucceeded = true;
          }
        },

        onleavestate: (event, from, to, eventTimestamp, eventData): any => {
          logStateMachineTransition('LEAVE', event, from, to);

          // Skip this JSM callback for JSM initial event
          // This event is fired when the state machine is constructed,
          // to transition us from 'none' to custom initial 'SETUP' state
          if (event === JSM_INITIAL_EVENT) {
            return;
          }

          if (from === State.BUFFERING) {
            this.resetBufferingHelpers();
          }

          // Skip this JSM callback when an JSM event without a timestamp is called,
          // because it would cause NPE in analytics code (state duration calculation etc.).
          // This probably has happened because the state machine was not used correctly,
          // aka. JSM event was not called via `callEvent` method as it should be!
          if (!eventTimestamp) {
            return;
          }

          const stateDuration = eventTimestamp - this.onEnterStateTimestamp;

          if (eventData && to !== State.PAUSED_SEEKING && to !== State.END_PLAY_SEEKING) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          }

          if (event === 'PLAY_SEEK' && from === State.PAUSE) {
            return true;
          }

          if (from === State.READY && to === State.STARTUP) {
            this.setVideoStartTimeout();
          } else if (from === State.STARTUP && (to === State.PLAYING || to === State.VIDEOSTART_FAILED)) {
            this.clearVideoStartTimeout();
          }

          const lowerCaseFrom = String(from).toLowerCase();
          if (to === State.VIDEOSTART_FAILED) {
            this.clearVideoStartTimeout();
            this.stateMachineCallbacks.videoStartFailed(stateDuration, createVideoStartFailedReason(event));
          } else if (from === State.PAUSED_SEEKING) {
            const seekDuration = eventTimestamp - this.seekTimestamp;
            this.stateMachineCallbacks.seeking(seekDuration);
          } else if (from === State.END_PLAY_SEEKING) {
            const seekDuration = this.seekedTimestamp - this.seekTimestamp;
            this.stateMachineCallbacks.seeking(seekDuration);
          } else if (event === Event.UNLOAD) {
            if (from === State.PLAYING) {
              this.stateMachineCallbacks.closedPlaying(stateDuration);
            } else {
              // we are only interested in last sample when in playing state for unload events
              return;
            }
          } else if (from === State.PAUSE && to !== State.PAUSED_SEEKING) {
            this.stateMachineCallbacks.pause(stateDuration, lowerCaseFrom);
          } else {
            // Default "onleavestate" state machine callback
            //
            // Every time, when we leave a state, we should try to call "state" callback,
            // to be able to make "a cut" and send analytics sample (if necessary)
            const defaultStateMachineCallbackFn = this.stateMachineCallbacks[lowerCaseFrom];
            if (isFunction(defaultStateMachineCallbackFn)) {
              // a try-catch block to add more resilience against callback errors
              try {
                defaultStateMachineCallbackFn(stateDuration, lowerCaseFrom, eventData);
              } catch (e) {
                logger.error('Exception occurred in State Machine callback ' + lowerCaseFrom, eventData, e);
              }
            } else {
              // if callback is not defined, log a warning to indicate to the developer that
              //  - a new callback function needs to be implemented to relay on this default state machine callback
              //  - or if no callback is needed, we should
              //    - early return from this JSM callback
              //    - or add this state to the ignored list
              logMissingCallbackWarning(from, [
                State.READY,
                State.SOURCE_CHANGING,
                State.MUTING_PAUSE,
                State.MUTING_PLAY,
                State.AUDIOTRACK_CHANGED_PLAYING,
                State.SUBTITLE_CHANGED_PLAYING,
              ]);
            }
          }

          if (eventData && to !== State.PAUSED_SEEKING && to !== State.PLAY_SEEKING && to !== State.END_PLAY_SEEKING) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (event === Event.MUTE) {
            this.stateMachineCallbacks.mute();
          } else if (event === Event.UN_MUTE) {
            this.stateMachineCallbacks.unMute();
          }
        },

        [`onenter${State.PLAYING}`]: () => {
          this.playingHeartbeatService.startHeartbeat();
        },

        [`onenter${State.QUALITY_CHANGE}`]: (_event, _from, _to, eventTimestamp, eventData) => {
          // Quality change should produce a "signal sample" and immediately transition us back to PLAYING state
          this.stateMachineCallbacks.qualitychange(eventData);
          this.stateMachine[Event.FINISH_QUALITY_CHANGE](eventTimestamp);
        },

        [`onenter${State.SOURCE_CHANGING}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.manualSourceChange(eventData);
          this.startupHasSucceeded = false;
        },

        onenterstate: (event, from, to, eventTimestamp, eventData) => {
          logStateMachineTransition('ENTER', event, from, to);

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();

          if (eventData && to !== State.PAUSED_SEEKING && to !== State.PLAY_SEEKING && to !== State.END_PLAY_SEEKING) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (event === Event.SEEK) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (event === Event.SEEKED) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          }

          if (
            event === 'PLAY_SEEK' &&
            to === State.PLAY_SEEKING &&
            to !== State.PLAY_SEEKING &&
            to !== State.END_PLAY_SEEKING
          ) {
            this.seekTimestamp = this.onEnterStateTimestamp;
          }

          if (event === Event.START_CAST && to === State.CASTING) {
            this.stateMachineCallbacks.startCasting(eventTimestamp, eventData);
          }

          if (to === State.BUFFERING) {
            this.startBufferingHeartbeatInterval();
          }
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.error(eventData);
        },

        [`onafter${Event.MANUAL_SOURCE_CHANGE}`]: (_event, from, to, _eventTimestamp, eventData) => {
          // To support the case: ADAPTER API SOURCE CHANGE VS. PLAYER SOURCE CHANGE
          // If the event was called while we are already in State.SOURCE_CHANGING, and we will stay there, it means:
          //  1) AdapterAPI.changeSource was called and now the "real" player source change is happening
          //  2) player source change was detected by InternalAdapter, and now the "later call" of AdapterAPI.changeSource
          //     is happening, so we should update the analytics config with the new one provided in the event data.
          if (from === State.SOURCE_CHANGING && to === State.SOURCE_CHANGING) {
            this.stateMachineCallbacks.manualSourceChange(eventData); // event data contains the new analytics config (if provided)
          }
        },

        [`onafter${Event.SEEK}`]: (_event, _from, _to, eventTimestamp, _eventData) => {
          this.seekTimestamp = eventTimestamp;
        },

        [`onafter${Event.SEEKED}`]: (_event, _from, _to, eventTimestamp, _eventData) => {
          this.seekedTimestamp = eventTimestamp;
        },

        onafterevent: (_event, _from, to, eventTimestamp, _eventData) => {
          if (to === State.MUTING_READY || to === State.MUTING_PLAY || to === State.MUTING_PAUSE) {
            this.stateMachine.FINISH_MUTING(eventTimestamp);
          }
          if (to === State.AUDIOTRACK_CHANGED_PLAYING) {
            this.stateMachine.FINISH_AUDIOTRACK_CHANGING(eventTimestamp);
          }
          if (to === State.SUBTITLE_CHANGED_PLAYING) {
            this.stateMachine.FINISH_SUBTITLE_CHANGING(eventTimestamp);
          }
        },
      },
    });
  }
}
